import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '@app/core';
import { LocalDataSource } from 'ng2-smart-table';
import { ToastrService } from 'ngx-toastr';
import { find } from 'lodash';
import {
  Documento,
  Revision,
  RevisionDocumento,
  Tarea,
  TareaAsociacion,
  TareaCondicion,
  UsuarioActual,
  FuncionDocumento,
  DocumentoDetalle,
  HistorialDocumento,
  Const,
} from '@app/shared';
import { ApproveSignConfig } from './shared/approve-sign-config.service';
import { ApproveSignDataService } from './shared/approve-sign-data.service';
import { from } from 'rxjs/observable/from';
import { Subject } from 'rxjs';

interface OptionCondiction {
  id_opcion: number;
  opcion: string;
  label: string;
}

@Component({
  selector: 'iw-approve-sign',
  templateUrl: './approve-sign.component.html',
  styleUrls: ['./approve-sign.component.scss'],
  providers: [ApproveSignDataService]
})
export class ApproveSignComponent implements OnInit, OnDestroy {

  // Define si la accion a relaizar es aprobar
  approve = false;
  // Define si la accion a relaizar es firmar
  sign = false;
  // Alias de la condicion a renderizar
  @Input() aliasCondiction: string;
  // Tarea
  @Input() task: Tarea;
  // Define cuando todos los documentos estan aprobados
  @Input() valid = false;
  // Define cuando el componente se instancia en una tarea de solo revision
  @Input() single = true;
  // Define si el usuario en session es responsable de la tarea
  @Input() userResponsible = false;
  // pregunta de aprobación
  question = '¿Informacion completa?:';
  // Nombre documento seleccionado
  documentSelect: Documento;
  // Define ecuando mostrar el combo para la respuesta de aprobacion o firma
  action = false;
  // Define cuando se ve las revisiones de los documentos
  approveDocuments = true;
  // Activa el campo de descripcion
  description = false;
  // Estdo de la tarea
  statusTask = true;
  // Define cuando se esta realizando una accion en los botones de la tabla
  buttonAction = false;
  // Usuario actual en sesion
  user: UsuarioActual;
  // Condicion de la tarea
  taskCondiction: Array<TareaCondicion> = [];
  // Revisiones del documento
  reviewDocuments: Array<RevisionDocumento>;
  // Documentos de la tarea de asociacion
  source: Array<Documento> = [];
  // Datos para llenar la tabla
  sourceData: LocalDataSource;
  // Opciones de respuesta
  options: OptionCondiction[] = [];
  // Tarea asociada
  taskAssociation: Array<TareaAsociacion>;
  // Tarea asociada
  documentsDownload: Array<any> = [];
  // Configuracion de la tabla
  settings = {};
  // formulario de respuesta
  formRD: FormGroup = new FormGroup({
    response: new FormControl('', [Validators.required]),
    description: new FormControl('')
  });
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();
  // Se emite cuando se aprueban o firman los documentos para actualizar la informacion
  @Output() docsUdate: EventEmitter<any> = new EventEmitter();

  constructor(
    private asdt: ApproveSignDataService,
    private toastr: ToastrService,
    private as: AuthenticationService) { }

  ngOnInit() {
    if (this.task.tarea_estado.nombre === 'finalizado' || !this.userResponsible) {
      this.statusTask = false;
    }
    // Se valida si en los parametros de la tarea viene la propiedad de aprobar
    if (this.task.tarea_forma[0].parametros.aprobar) {
      this.approve = this.task.tarea_forma[0].parametros.aprobar;
    }
    // Se valida si en los parametros de la tarea viene la propiedad de firmar
    if (this.task.tarea_forma[0].parametros.firmar) {
      this.sign = this.task.tarea_forma[0].parametros.firmar;
    }
    this.taskAssociation = this.task.tarea_asociacion;
    this.taskCondiction = <TareaCondicion[]>this.task.tarea_condicion;
    const docsC = new ApproveSignConfig(this.approve, this.sign);
    this.settings = docsC.settings;
    docsC.accion.subscribe((data: any) => {
      this.buttonAction = true;
      this.accionTable(data);
    });
    this.loadingDocs();
    this.loadingCondiction();
    // Obtener usuario autenticado
    this.user = this.as.getCredentials();
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Se encarga de controlar el evento de clic en el las filas de la tabla
   *
   * @param {*} data
   * @memberof ApproveSignComponent
   */
  rowSelect(data: any) {
    if (!this.buttonAction) {
      if (this.statusTask && this.userResponsible) {
        this.documentSelect = data.data;
        this.formRD.reset({ response: '', description: '' });
        this.description = false;
        if (
          this.documentSelect &&
          this.documentSelect.documentos_detalle &&
          this.documentSelect.documentos_detalle.length
        ) {
          for (let index = 0; index < this.documentsDownload.length; index++) {
            if (this.documentsDownload[index] === this.documentSelect.documentos_detalle[0].id_documento) {
              this.action = true;
              break;
            } else {
              this.action = false;
            }
          }
        } else {
          this.action = false;
        }
        if (!this.action) {
          this.toastr.warning('Para dar respuesta a este documento debe descargarlo primero', 'Acción no permitida', {
            positionClass: 'toast-top-center'
          });
        }
      }
    } else {
      this.buttonAction = false;
    }
  }

  /**
   * Funcion de regresar para el componente de ver revisiones
   *
   * @param {*} data
   * @memberof ApproveSignComponent
   */
  reviewBack(data: any) {
    this.approveDocuments = data;
  }

  /**
   * Se ejecuta cada vez que se realiza un cambio de opcion en el select de las opciones de respuesta
   * para mostrar el campo de descripcion dependiendo de la opcion seleccionada
   *
   * @memberof ApproveSignComponent
   */
  changeResponse() {
    const response = find(this.options, {id_opcion: this.formRD.value.response});
    if (response.opcion.toLowerCase() === 'no') {
      this.description = true;
      this.formRD.controls.description.setValidators([Validators.required]);
    } else {
      this.description = false;
      this.formRD.controls.description.clearValidators();
      this.formRD.controls.description.reset();
    }
  }

  /**
   * Guarda la respuesta y actualiza la informacion de los documentos y la tarea
   *
   * @memberof ApproveSignComponent
   */
  saveResponse() {
    let idHistory = 0;
    this.documentSelect.documentos_detalle.forEach(document => {
      if (document.historial.length > 0) {
        const version = document.historial.length;
        document.historial.forEach(history => {
          if (history.version === version) {
            idHistory = history.id;
          }
        });
      }
    });
    const review: Revision = {
      descripcion: this.formRD.value.description ? this.formRD.value.description : '',
      documento_historial: idHistory,
      tarea: this.task.id,
      responsable: this.user.user_id,
      revision: false,
      opcion: this.formRD.value.response
    };
    const response = find(this.options, {id_opcion: this.formRD.value.response});
    if (response.opcion.toLowerCase() === 'no') {
      review.revision = false;
    } else {
      review.revision = true;
    }
    this.asdt.saveReviewDocument(review).subscribe((res: any) => {
      for (let indexSource = 0; indexSource < this.source.length; indexSource++) {
        if (this.source[indexSource].clasificacion_id === this.documentSelect.clasificacion_id) {
          for (let indexDetail = 0;
            indexDetail < this.source[indexSource].documentos_detalle.length;
            indexDetail++) {
            for (let indexHistory = 0;
              indexHistory < this.source[indexSource].documentos_detalle[indexDetail].historial.length;
              indexHistory++) {
              if (this.source[indexSource]
                .documentos_detalle[indexDetail]
                .historial[indexHistory].id === idHistory) {
                this.source[indexSource]
                  .documentos_detalle[indexDetail]
                  .historial[indexHistory]
                  .revision.push({
                    id_revision: res.id,
                    id_tarea: res.tarea,
                    nombre_tarea: this.task.nombre,
                    reponsable_id: res.responsable,
                    descripcion: res.descripcion,
                    fecha: res.fecha,
                    revision: res.revision,
                    historial_id: res.documento_historial,
                    nombre_responsable: this.user.info_persona.nombres,
                    apellidos_responsable: this.user.info_persona.apellidos,
                    opcion: this.formRD.value.response
                  });
                  this.source[indexSource].documentos_detalle[indexDetail].historial[indexHistory]
                    .revision.sort((a, b) => b.id_revision - a.id_revision);
              }
            }
          }
        }
      }
      this.docsUdate.emit(this.source);
      this.formRD.reset({ response: '', description: '' });
      this.description = false;
      this.action = false;
      this.sourceData = new LocalDataSource(this.source);
      this.sourceData.refresh();
      this.toastr.success('La accion se a realizado con exito', 'Accion exitosa');
    });
  }

  /**
   * Cargar los documentos de la tarea.
   *
   * @memberof ApproveSignComponent
   */
  loadingDocs() {
    this.taskAssociation.forEach(task => {
      task.documentos.forEach((doc: Documento) => {
        // Copiando funciones del documento
        const functionD: FuncionDocumento[] = [];
        if (doc.funciones && doc.funciones.length) {
          doc.funciones.forEach(fun => {
            functionD.push({
              funcion: fun.funcion,
              id_funcion: fun.id_funcion,
              obligatorio: fun.obligatorio
            });
          });
        }
        // Copiando documentos detalle
        const detailDocument: DocumentoDetalle[] = [];
        if (doc.documentos_detalle && doc.documentos_detalle.length) {
          doc.documentos_detalle.forEach(detail => {
            // Copiando historial
            const record: HistorialDocumento[] = [];
            if (detail.historial && detail.historial.length) {
              detail.historial.forEach(history => {
                // Copiando revisiones
                const revisions: RevisionDocumento[] = [];
                if (history.revision && history.revision.length) {
                  history.revision.forEach(revision => {
                    revisions.push({
                      id_revision: revision.id_revision,
                      id_tarea: revision.id_tarea,
                      reponsable_id: revision.reponsable_id,
                      nombre_tarea: revision.nombre_tarea,
                      descripcion: revision.descripcion,
                      nombre_responsable: revision.nombre_responsable,
                      apellidos_responsable: revision.apellidos_responsable,
                      fecha: revision.fecha,
                      revision: revision.revision,
                      historial_id: revision.historial_id,
                      version: revision.version,
                      opcion: revision.opcion
                    });
                  });
                }
                record.push({
                  id: history.id,
                  nombre: history.nombre,
                  nombre_archivo: history.nombre_archivo,
                  extension: history.extension,
                  version: history.version,
                  revision: revisions,
                  radicado: history.radicado,
                });
              });
            }
            detailDocument.push({
              fecha: detail.fecha,
              folios: detail.folios,
              historial: record,
              id_documento: detail.id_documento,
              radicado: detail.radicado,
              documento_cita: detail.documento_cita,
              id_documento_cita: detail.id_documento_cita,
            });
          });
        }
        // Copiando documento
        const document: Documento = {
          clasificacion: doc.clasificacion,
          clasificacion_id: doc.clasificacion_id,
          codigo_documento: doc.codigo_documento,
          documentos_detalle: detailDocument,
          funciones: functionD,
          id_tipo_documental: doc.id_tipo_documental,
          id_expediente_cita: doc.id_expediente_cita,
          ruta_generacion: doc.ruta_generacion,
          ruta_vista_previa: doc.ruta_vista_previa,
          tipo_documental: doc.tipo_documental,
          idtarea: task.id_tarea_destino,
        };
        for (let index = 0; index < doc.funciones.length; index++) {
          if (this.approve) {
            if (doc.funciones[index].funcion === Const.DOCUMENTS_FUNCTIONS.approve) {
              this.source.push(document);
              break;
            }
          } else if (this.sign) {
            if (doc.funciones[index].funcion === Const.DOCUMENTS_FUNCTIONS.sign) {
              this.source.push(document);
              break;
            }
          }
        }
      });
    });
    this.sourceData = new LocalDataSource(this.source);
  }

  /**
   * Carga la pregunta y las condiciones
   *
   * @private
   * @memberof ApproveSignComponent
   */
  private loadingCondiction() {
    if (this.taskCondiction.length > 0) {
      let condiction: TareaCondicion;
      if (this.aliasCondiction) {
        condiction = find(this.taskCondiction, {alias: this.aliasCondiction});
      }
      if (!condiction) {
        condiction = this.taskCondiction[0];
      }
      this.question = condiction.tarea_condicion;
      this.taskCondiction.forEach(con => {
        if (con.alias === condiction.alias) {
          this.options.push({
            id_opcion: con.id_opcion,
            opcion: con.opcion,
            label: con.mascara ? con.mascara : con.opcion
          });
        }
      });
    }
  }

  /**
   * Controla las acciones en la tabla de los botones
   *
   * @private
   * @param {*} data
   * @returns {*}
   * @memberof ApproveSignComponent
   */
  private accionTable(data: any): any {
    if (data.accion === 'download') {
      if (data.data['documento_cita']) {
        this.documentsDownload.push(data.data['id_documento']);
        const a = document.createElement('a');
        a.href = Const.ROUTES_SYSTEM_INTEGRATIONS.cita.dowloadDocument + data.data['documento_cita'];
        a.click();
        a.remove();
      } else if (data.data['id_documento']) {
        this.download(data.data['id_documento']);
      }
    } else if (data.accion === 'review') {
      this.reviewDocument(data.data);
    } else if (data.accion === 'generate') {
      this.generateDocument(data.data);
    } else if (data.accion === 'error') {
      this.toastr.error(data.message, 'Error de accion');
    }
  }

  /**
   * Descarga el documento y le informa al usuario que el documento esta listo para realizar la accion
   *
   * @private
   * @param {number} data
   * @returns {*}
   * @memberof ApproveSignComponent
   */
  private download(data: number): any {
    this.asdt.dowloadFile(data).subscribe((res: any) => {
      const a = document.createElement('a');
      a.href = res.file.path;
      a.target = '_blank';
      a.download = res.file.name;
      a.click();
      a.remove();
      const control = this.documentsDownload.findIndex(item => {
        return item === data;
      });
      if (control === -1 && this.statusTask) {
        this.documentsDownload.push(data);
        if (this.userResponsible) {
          if (this.approve && this.sign) {
            this.toastr.success('Ya puede aprobar y firmar el documento', 'Acción activada', {
              positionClass: 'toast-top-center'
            });
          } else {
            if (this.approve) {
              this.toastr.success('Ya puede aprobar el documento', 'Accion activada', {
                positionClass: 'toast-top-center'
              });
            } else if (this.sign) {
              this.toastr.success('Ya puede firmar el documento', 'Accion activada', {
                positionClass: 'toast-top-center'
              });
            } else {
              this.toastr.success('Ya puede realizar la accion sobre el documento', 'Accion activada', {
                positionClass: 'toast-top-center'
              });
            }
          }
        }
      }
    });
  }

  /**
   * Carga la informacion de el componente de revisiones y lo activa
   *
   * @private
   * @param {*} data
   * @returns {*}
   * @memberof ApproveSignComponent
   */
  private reviewDocument(data: any): any {
    this.reviewDocuments = data;
    this.approveDocuments = false;
  }

  /**
   * Genera el documento seleccionado
   *
   * @private
   * @param {*} data
   * @memberof ApproveSignComponent
   */
  private generateDocument(data: any) {
    const dataDocument = {
      tarea: data.idtarea,
      documento: data.clasificacion_id
    };

    this.asdt.generateDocument(dataDocument).subscribe(file => {
      const a = document.createElement('a');
      a.target = '_blank';
      a.href = file.url.path;
      a.download = file.url.name;
      a.click();
      a.remove();
    });

  }
}



// WEBPACK FOOTER //
// ./src/app/forms/shared/approve-sign/approve-sign.component.ts